import { Injectable, NgZone } from '@angular/core';
import { Subject } from 'rxjs';

/**
 * HERE map service provides functionalities to interact
 * with the map.
 *
 * @see https://developer.here.com/documentation/maps/3.1.19.2/dev_guide/index.html
 */

@Injectable()
export class HereMapService {
  private _platform;
  private _map: H.Map;

  private _mapReady = new Subject<boolean>();
  mapReady$ = this._mapReady.asObservable();

  constructor(private _ngZone: NgZone) {}

  /**
   * Create HERE map instance
   *
   * @param apiKey - javascript API key from Invoice2go HERE account
   * @param mapContainer - HTML element to display map
   * @param options - map options
   */
  createMap(
    apiKey: string,
    mapContainer: HTMLElement,
    options: H.Map.Options = {}
  ) {
    this._platform = this._ngZone.runOutsideAngular(
      () =>
        new H.service.Platform({
          apikey: apiKey,
          useHTTPS: true,
        })
    );

    const defaultMapLayers = this._ngZone.runOutsideAngular(() =>
      this._platform.createDefaultLayers()
    );

    this._map = this._ngZone.runOutsideAngular(
      () => new H.Map(mapContainer, defaultMapLayers.vector.normal.map, options)
    );

    this._mapReady.next(true);
  }

  /**
   * Add marker on the map
   * @param lat - latitude
   * @param lng - longitude
   */
  addMarker(lat: number, lng: number) {
    this._ngZone.runOutsideAngular(() => {
      const newMarker = new H.map.Marker({ lat, lng });
      this._map.addObject(newMarker);
    });
  }

  /**
   * Update latitude and longitude for map
   * @param lat - latitude
   * @param lng - longitude
   */
  setCenter(lat: number, lng: number) {
    this._ngZone.runOutsideAngular(() => {
      this._map.setCenter({ lat, lng });
    });
  }

  /**
   * Update zoom level for map
   * @param lat - latitude
   * @param lng - longitude
   */
  setZoom(zoom: number) {
    this._ngZone.runOutsideAngular(() => {
      this._map.setZoom(zoom);
    });
  }
}
